import { useEffect, useState } from 'react';
import { Button, Dialog, Input, Select, Textarea } from '@/ds';
import { ApiError } from '@/api/client';
import { useOperation, type WalletKind, type WalletSummary } from '@/api/hooks';
import { useControlSize } from '@/lib/useControlSize';

const KINDS = [
  { value: 'virtual_card', label: 'Виртуальная карта' },
  { value: 'crypto', label: 'Крипто-кошелёк' },
  { value: 'direct_card', label: 'Обычная карта' },
  { value: 'other', label: 'Другое' },
];

/**
 * Правка кошелька. Создать кошелёк здесь нельзя: кошельки описаны в коде
 * (docs/04-finance.md). У кошелька из реестра меняются только заметки,
 * название и тип задаёт описание.
 */
export function WalletFormDialog({
  wallet,
  open,
  onClose,
}: {
  wallet: WalletSummary;
  open: boolean;
  onClose: () => void;
}) {
  const size = useControlSize();
  const update = useOperation<{
    id: string;
    name?: string;
    kind?: WalletKind;
    notes: string | null;
  }>('wallet.update');

  const coded = wallet.code !== null;
  const [name, setName] = useState('');
  const [kind, setKind] = useState<WalletKind>('virtual_card');
  const [notes, setNotes] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(wallet.name);
    setKind(wallet.kind);
    setNotes(wallet.notes ?? '');
    setLocalError(null);
    update.reset();
    // Сброс делается один раз при открытии.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, wallet.id]);

  const serverError = update.error instanceof ApiError ? update.error.message : null;

  function submit() {
    setLocalError(null);
    if (!coded && name.trim().length === 0) {
      setLocalError('Название не может быть пустым');
      return;
    }
    update.mutate(
      {
        id: wallet.id,
        ...(coded ? {} : { name: name.trim(), kind }),
        notes: notes.trim() || null,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open={open}
      title="Изменить кошелёк"
      description={
        coded
          ? 'Название, тип и источник данных задаются в коде, в реестре кошельков'
          : 'Валюта не меняется: от неё зависят все прошлые расчёты'
      }
      onClose={onClose}
      width={560}
      footer={
        <>
          <Button variant="secondary" size={size} onClick={onClose}>
            Отмена
          </Button>
          <Button size={size} onClick={submit} loading={update.isPending}>
            Сохранить
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <Input
          size={size}
          label="Название"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={coded}
          {...(coded ? { help: 'Меняется только в коде' } : {})}
        />
        <Select
          size={size}
          label="Тип"
          options={KINDS}
          value={kind}
          onChange={(e) => setKind(e.target.value as WalletKind)}
          disabled={coded}
        />
        <Textarea
          label="Заметки"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          help="Необязательно: реквизиты, особенности, ограничения"
        />
        {localError ?? serverError ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--status-danger-fg)' }}>
            {localError ?? serverError}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
